import * as React from "react";

import { toast } from "@replo/design-system/components/toast/Toast";
import { useCurrentChat } from "@v2/features/chat/hooks/useCurrentChat";
import { createChatFileAttachments } from "@v2/features/chat/utils/chatFileAttachment";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { useRequiredContext } from "replo-utils/react/context";

const MAX_FILE_ATTACHMENTS = 20;

type MessagePopoverType = "inspiration" | "contextMenu" | null;

const CurrentChatContext = React.createContext<
  | (ReturnType<typeof useCurrentChat> & {
      messagePopoverType: MessagePopoverType;
      dispatchCurrentChat: (action: CurrentChatAction) => void;
      isContextMenuOpen: boolean;
      addFiles: (files: File[] | null) => Promise<void>;
    })
  | undefined
>(undefined);

type CurrentChatAction =
  | {
      type: "openContextMenu";
    }
  | {
      type: "closeContextMenu";
    }
  | {
      type: "closeInspirationMenu";
    }
  | {
      type: "openInspirationMenu";
    };

export const CurrentChatProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const result = useCurrentChat();

  const [messagePopoverType, setMessagePopoverType] = React.useState<
    "inspiration" | "contextMenu" | null
  >(null);

  const addFiles = React.useCallback(
    async (newFiles: File[] | null) => {
      if (!newFiles || newFiles.length === 0) {
        return;
      }

      const attachments = await createChatFileAttachments(newFiles);

      result.setFiles((currentFiles) => {
        const currentCount = currentFiles?.length ?? 0;
        const newCount = attachments.length;

        if (currentCount + newCount > MAX_FILE_ATTACHMENTS) {
          toast.error({
            title: "Too many attachments",
            description: `You can add at most ${MAX_FILE_ATTACHMENTS} attachments to a message.`,
          });
          return currentFiles;
        }

        if (currentFiles === null) {
          return attachments;
        }
        return [...currentFiles, ...attachments];
      });
    },
    [result.setFiles],
  );

  const dispatchCurrentChat = (action: CurrentChatAction) => {
    return exhaustiveSwitch(
      action,
      "type",
    )({
      openContextMenu: () => {
        setMessagePopoverType("contextMenu");
      },
      closeContextMenu: () => {
        if (messagePopoverType === "contextMenu") {
          setMessagePopoverType(null);
        }
      },
      closeInspirationMenu: () => {
        if (messagePopoverType === "inspiration") {
          setMessagePopoverType(null);
        }
      },
      openInspirationMenu: () => {
        setMessagePopoverType("inspiration");
      },
    });
  };

  return (
    <CurrentChatContext.Provider
      value={{
        ...result,
        messagePopoverType,
        dispatchCurrentChat,
        isContextMenuOpen: messagePopoverType === "contextMenu",
        addFiles,
      }}
    >
      {children}
    </CurrentChatContext.Provider>
  );
};

export const useCurrentChatContext = () => {
  return useRequiredContext(CurrentChatContext);
};
